
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Users, UserCheck, Building, Filter } from "lucide-react";
import { useAllInfluencers } from '@/hooks/useAllInfluencers';
import { useContacts } from '@/hooks/useContacts';
import { useBrands } from '@/hooks/useBrands';

interface RecipientSelectorProps {
  selectedRecipients: any[];
  onRecipientsChange: (recipients: any[]) => void;
}

const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  selectedRecipients,
  onRecipientsChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('influencers');
  
  const { data: influencers = [] } = useAllInfluencers();
  const { data: contacts = [] } = useContacts();
  const { data: brands = [] } = useBrands();

  const handleRecipientToggle = (recipient: any, type: string) => {
    const recipientWithType = { ...recipient, type };
    const isSelected = selectedRecipients.some(r => r.id === recipient.id && r.type === type);
    
    if (isSelected) {
      onRecipientsChange(selectedRecipients.filter(r => !(r.id === recipient.id && r.type === type)));
    } else {
      onRecipientsChange([...selectedRecipients, recipientWithType]);
    }
  };

  const handleSelectAll = (items: any[], type: string) => {
    const filteredItems = items.filter(item => {
      const searchText = searchTerm.toLowerCase();
      return (
        item.name?.toLowerCase().includes(searchText) ||
        (type === 'contact' && item.email?.toLowerCase().includes(searchText)) ||
        (type === 'influencer' && item.handle?.toLowerCase().includes(searchText))
      );
    });
    
    const itemsWithType = filteredItems.map(item => ({ ...item, type }));
    const currentTypeRecipients = selectedRecipients.filter(r => r.type !== type);
    onRecipientsChange([...currentTypeRecipients, ...itemsWithType]);
  };

  const handleClearAll = (type: string) => {
    onRecipientsChange(selectedRecipients.filter(r => r.type !== type));
  };

  const filterItems = (items: any[], type: string) => {
    return items.filter(item => {
      const searchText = searchTerm.toLowerCase();
      return (
        item.name?.toLowerCase().includes(searchText) ||
        (type === 'contact' && item.email?.toLowerCase().includes(searchText)) ||
        (type === 'influencer' && item.handle?.toLowerCase().includes(searchText))
      );
    });
  };

  const isSelected = (item: any, type: string) => {
    return selectedRecipients.some(r => r.id === item.id && r.type === type);
  };

  const getItemEmail = (item: any, type: string) => {
    if (type === 'contact') {
      return item.email || 'No email available';
    }
    if (type === 'influencer') {
      // Influencers might not have email in the current schema
      return item.contactEmail || 'Contact via social media';
    }
    if (type === 'brand') {
      return item.contactEmail || 'No email available';
    }
    return 'No email available';
  };

  const RecipientList = ({ items, type, title }: { items: any[], type: string, title: string }) => {
    const filteredItems = filterItems(items, type);
    const selectedCount = selectedRecipients.filter(r => r.type === type).length;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{title}</h3>
            <Badge variant="secondary">{selectedCount} selected</Badge>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleSelectAll(filteredItems, type)}
              disabled={filteredItems.length === 0}
            >
              Select All
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleClearAll(type)}
              disabled={selectedCount === 0}
            >
              Clear All
            </Button>
          </div>
        </div>
        
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {filteredItems.map((item) => (
            <div 
              key={item.id} 
              className="flex items-center space-x-3 p-2 border rounded-lg hover:bg-gray-50"
            >
              <Checkbox
                checked={isSelected(item, type)}
                onCheckedChange={() => handleRecipientToggle(item, type)}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {getItemEmail(item, type)}
                  {item.handle && ` • @${item.handle}`}
                  {item.company_name && ` • ${item.company_name}`}
                </p>
              </div>
            </div>
          ))}
          {filteredItems.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              No {title.toLowerCase()} found
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Select Recipients
        </CardTitle>
        <CardDescription>
          Choose who will receive your bulk email
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search recipients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Selected Recipients Summary */}
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
          <UserCheck className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            {selectedRecipients.length} recipients selected
          </span>
        </div>

        {/* Recipient Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="influencers">Influencers</TabsTrigger>
            <TabsTrigger value="contacts">Contacts</TabsTrigger>
            <TabsTrigger value="brands">Brands</TabsTrigger>
          </TabsList>

          <TabsContent value="influencers" className="mt-4">
            <RecipientList 
              items={influencers} 
              type="influencer" 
              title="Influencers" 
            />
          </TabsContent>

          <TabsContent value="contacts" className="mt-4">
            <RecipientList 
              items={contacts.filter(c => c.email)} 
              type="contact" 
              title="Contacts" 
            />
          </TabsContent>

          <TabsContent value="brands" className="mt-4">
            <RecipientList 
              items={brands.filter(b => b.name)} 
              type="brand" 
              title="Brands" 
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default RecipientSelector;
